import moment from 'moment'
import { asElement, asArray } from "./arrayUtils"
import Idea, {fieldEditable} from "@/types/Idea"
import IdeaProperty, {computingNow, defaultable, defaultValueOf} from "@/types/IdeaProperty"
import Domain, {domainOfProperty} from "@/types/Domain"
import {travelsAsList} from "@/utils/propertyDefinition"
import {isEmpty} from "@/utils/propertyDisplay"

function isNullOrUndefined(o: any): o is null | undefined {
    return o === null || o === undefined
}

/**
 * O que separa as partes de um identificador composto.
 *
 * Ponto médio, e não hífen: hífen é sinal de palavra composta e se confunde com o que está
 * DENTRO de cada parte. Quem compõe de verdade é o servidor, na leitura; aqui a constante
 * existe para as telas que montam a MESMA composição antes de gravar — a prévia do modal de
 * criar e o título do card — dizerem a mesma coisa.
 */
export const IDENTIFIER_SEPARATOR = ' · '

/** As propriedades que compõem a chave, na ordem em que a coleção as declara. */
export function identifierProperties(idea: Idea): IdeaProperty[] {
    return (idea.properties || []).filter(property => property.identifier)
}

/**
 * A chave tem mais de um campo?
 *
 * É o que decide se o identificador merece coluna própria numa grade: com um campo só ele sairia
 * idêntico à coluna ao lado, e coluna que repete a vizinha não informa nada.
 */
export function hasCompositeIdentifier(idea: Idea): boolean {
    return identifierProperties(idea).length > 1
}

/**
 * Como uma instância se chama na tela.
 *
 * Sem campo-chave preenchido cai na mesma composição que a mensagem de exclusão usa, para
 * a instância continuar identificável. Fica aqui porque a ficha e a edição mostram o
 * mesmo título, e a regra do fallback não pode divergir entre as duas.
 */
export function titleOfInstance(instance: { _identifier?: string; _instance?: number }, idea: Idea): string {
    return instance._identifier || `${idea.name} #${instance._instance}`
}

type TypeConsistFunction = (v: any) => any

/**
 * Um id: o da opção, na seleção, e o da conta, no atributo de usuário — que o servidor confere contra
 * o domínio ou contra as pessoas do ambiente. String numérica vira número porque o servidor recusa
 * "3" onde espera 3.
 */
const asId: TypeConsistFunction = (v) => {
    const id = typeof v === 'number' ? v : Number(v)
    return Number.isInteger(id) ? id : null
}

const typeConsists: Record<string, TypeConsistFunction> = {
    text: (v) => v.toString(),
    date: (v) => moment(v.toString(), 'YYYY-MM-DD', true).isValid() ? v.toString() : null,
    number: (v) => Number(v),
    boolean: (v) => v === true || v === 'true',
    //o valor de uma mídia é sempre a URL dela, nunca o arquivo: sem esta guarda um
    //Blob que escapasse do campo viraria a string "[object Blob]" no corpo do POST
    image: (v) => typeof v === 'string' ? v : null,
    audio: (v) => typeof v === 'string' ? v : null,
    choice: asId,
    user: asId
}

function consistantProperty(propertyValue: any, property: IdeaProperty): any {
    //Vazio é vazio, inclusive no sim/não: "nada escolhido" é o terceiro estado dele, e o campo de
    //dois botões o representa. Quando era uma caixa de marcar, o vazio virava Não aqui, ao criar e
    //ao salvar — e o obrigatório nunca tinha como acusar que ninguém escolheu
    if (isNullOrUndefined(propertyValue)) return null
    const consist = typeConsists[property.type || '']
    if (!consist) return propertyValue
    return consist(propertyValue)
}

/**
 * O valor de um relacionamento como ele deve viajar ao servidor.
 *
 * **Lista não leva vazio.** Um `null` numa lista de vínculos não aponta para nada, e gravado ele
 * volta como um candidato que não existe: o campo morre ao desenhá-lo e some da tela. Era o caso
 * de uma canção com `parceria: [1, null]`, e ele se perpetuava sozinho — lido de volta, o
 * componente montava um item fantasma, e o salvar seguinte o gravava de novo.
 *
 * **Escalar nulo continua passando**, e a diferença é o ponto: ali o nulo É o valor — "sem
 * vínculo" —, e o servidor precisa recebê-lo para limpar o campo. Numa lista, "sem vínculo" se
 * diz tirando o item, não guardando um buraco no lugar dele.
 *
 * Mora aqui, e não em cada caminho de escrita, porque **são dois** — criar passa pelo
 * `consistantInstance`, editar pelo `instanceDiff` — e nenhum dos dois limpava. Com a regra num
 * lugar só, os dois dizem a mesma coisa.
 */
export function relationshipValue(value: any, property: any): any {
    //Lista quando o campo é lista — ver o `travelsAsList`. No reverso o backend escreve no
    //outro lado, e o valor sempre viaja como array de ids.
    const ids = travelsAsList(property) ? asArray(value) : value
    return Array.isArray(ids) ? ids.filter(id => !isNullOrUndefined(id)) : ids
}

export function consistantInstance(instance: Record<string, any>, idea: Idea): Record<string, any> {
    const result: Record<string, any> = {}
    for (const property of idea.properties) {
        //Nem a calculada, nem o "Calculado quando" enquanto é calculado: o valor dele é da fórmula
        if (computingNow(instance, property)) continue

        const value = instance[property.alias]

        if (property.relationship) {
            result[property.alias] = relationshipValue(value, property)
        } else if (property.multiple) {
            result[property.alias] = asArray(value).map(v => consistantProperty(v, property)).filter(v => !isNullOrUndefined(v))
        } else {
            result[property.alias] = consistantProperty(asElement(value), property)
        }
    }

    if (idea._workflow && instance._state && idea._workflow.states.find(s => s.id === instance._state)) {
        result._state = instance._state
    }

    return result
}

/**
 * Põe a situação inicial do fluxo numa instância que ainda não tem uma.
 *
 * Mora aqui, ao lado do consistantInstance, porque os dois leem o mesmo
 * `idea._workflow` e precisam concordar sobre o que é uma situação válida.
 */
export function seedState(instance: Record<string, any>, idea: Idea): void {
    const workflow = idea._workflow
    if (!workflow || instance._state) return
    const initial = workflow.states.find(state => state.initial)
    if (initial) instance._state = initial.id
}

/**
 * O campo exige valor NESTE item? Lê as escritas que a obrigatoriedade tem.
 *
 * No campo único é o `required` (sempre) ou o `requiredWhen` ("Obrigatório quando"), e por isso a
 * pergunta precisa do item, e não só da definição. Quem avalia a fórmula é o servidor: o item lido e a
 * prévia trazem os campos obrigatórios agora (`_required`). No múltiplo, obrigatório se escreve
 * `itemCount.min >= 1` — e, com o "Obrigatório quando", o mínimo só vale quando a condição dá Sim.
 *
 * Estava inteira no template do InstanceInput, onde o campo decide se mostra o asterisco. Virou
 * função ao ganhar um segundo leitor — o `seedDefaults` —, porque uma regra com duas escritas é
 * exatamente a que não pode ser copiada: a segunda cópia esqueceria a condição.
 */
export function isRequired(instance: Record<string, any>, property: IdeaProperty): boolean {
    //Só de quem pode editar o campo (decisão do usuário): o servidor também não cobra
    if (!fieldEditable(instance, property)) return false
    const now = property.requiredWhen ? asArray(instance._required).includes(property.alias) : undefined
    if (property.multiple) return !!(property.itemCount && property.itemCount.min > 0) && now !== false
    return property.required === true || !!now
}

/**
 * Os campos que nascem com o padrão — fixo ou por fórmula: os que têm padrão, estão vazios e, ao editar
 * (`onlyRequired`), são obrigatórios.
 *
 * `onlyRequired` é a diferença entre criar e editar. Criando, o padrão é o ponto de partida de todo
 * campo. Editando um item antigo que está sem valor, só o campo OBRIGATÓRIO é preenchido: ele já não
 * poderia ser salvo vazio, então o padrão é ajuda. No campo opcional, vazio é uma resposta legítima, e
 * preenchê-lo seria reescrever dado que ninguém pediu.
 *
 * Um dono para a regra: o `seedDefaults` semeia o fixo por ela, e o formulário segue por ela os padrões
 * por fórmula (o `defaulted` do `InstanceInput`).
 */
export function defaultedProperties(instance: Record<string, any>, idea: Idea, {onlyRequired = false} = {}): IdeaProperty[] {
    return idea.properties
        .filter(property => property.default && defaultable(property))
        .filter(property => isEmpty(instance[property.alias]))
        .filter(property => !onlyRequired || isRequired(instance, property))
}

/**
 * Põe o padrão FIXO dos campos que nascem com ele (`defaultedProperties`) numa instância que ainda não os
 * tem. O por fórmula vem da prévia do servidor — ver o `InstanceInput`.
 *
 * Irmão do `seedState`, e pela mesma razão de vizinhança: os dois semeiam o que a DEFINIÇÃO
 * manda, e precisam concordar sobre o que é um valor válido.
 *
 * **Resolve contra o que existe agora**: na seleção, as opções do domínio; no relacionamento, os
 * candidatos do campo (`candidates`, por coleção de destino, quando quem chama os tem). O id que não
 * casa com nenhum sai — a opção ou o item podem ter sido excluídos depois de o padrão ser configurado.
 * Semear um id pendurado gravaria um valor que o servidor recusa e que o campo não sabe desenhar: é o
 * mesmo veneno do vínculo vazio, por outro caminho. O `seedState` faz o mesmo ao só semear se achar o
 * estado inicial.
 */
export function seedDefaults(
    instance: Record<string, any>, idea: Idea, domains: Domain[] | undefined,
    {onlyRequired = false, candidates}: { onlyRequired?: boolean, candidates?: Record<string, Array<{ _instance: number }>> } = {}
): void {
    defaultedProperties(instance, idea, {onlyRequired}).forEach(property => {
        const value = defaultValueOf(property)
        if (value === undefined) return
        const known = property.type === 'choice' && !property.relationship
            ? (domainOfProperty(property, domains)?.options || []).map(option => option.id)
            : property.relationship && candidates ? (candidates[property.relationship.idea] || []).map(item => item._instance)
                : undefined
        if (!known) {
            instance[property.alias] = value
            return
        }
        const ids = asArray(value).filter(id => known.includes(id))
        if (!ids.length) return
        instance[property.alias] = property.multiple ? ids : ids[0]
    })
}

export default { consistantInstance, seedState, seedDefaults }
